'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { useRouter } from 'next/navigation'
import Fuse from 'fuse.js'
import { Button } from '@/components/ui/button'

type Article = { slug: string; title: string; summary: string; tags: string[]; author: string }

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  )
}

export function SearchModal() {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [index, setIndex] = useState<Article[]>([])
  const [results, setResults] = useState<Article[]>([])
  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const fuseRef = useRef<Fuse<Article> | null>(null)

  // Load search index once on first open
  useEffect(() => {
    if (!open || index.length > 0) return
    fetch('/api/search')
      .then((r) => r.json())
      .then((data: Article[]) => {
        setIndex(data)
        fuseRef.current = new Fuse(data, {
          keys: ['title', 'summary', 'tags', 'author'],
          threshold: 0.35,
          includeScore: true,
        })
      })
      .catch(() => {})
  }, [open, index.length])

  // Search on query change
  useEffect(() => {
    if (!fuseRef.current || !query.trim()) {
      setResults(index.slice(0, 6))
      setActiveIdx(0)
      return
    }
    const hits = fuseRef.current
      .search(query)
      .slice(0, 8)
      .map((r) => r.item)
    setResults(hits)
    setActiveIdx(0)
  }, [query, index])

  // Global CMD+K / Ctrl+K
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen((o) => !o)
      }
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50)
      setQuery('')
    }
  }, [open])

  const navigate = useCallback(
    (slug: string) => {
      setOpen(false)
      router.push(`/articles/${slug}`)
    },
    [router],
  )

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && results[activeIdx]) {
      navigate(results[activeIdx].slug)
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        aria-label="Search articles (⌘K)"
        className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors"
      >
        <SearchIcon />
        <span className="hidden sm:inline">Search</span>
        <kbd className="bg-muted text-muted-foreground hidden rounded px-1 py-0.5 font-mono text-xs sm:inline">
          ⌘K
        </kbd>
      </button>
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
      onClick={() => setOpen(false)}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Modal */}
      <div
        className="bg-background relative z-10 w-full max-w-lg rounded-xl border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 border-b px-4 py-3">
          <SearchIcon />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search articles…"
            className="flex-1 bg-transparent text-sm outline-none"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            className="text-muted-foreground px-2 py-1 text-xs"
          >
            Esc
          </Button>
        </div>

        {/* Results */}
        {results.length > 0 ? (
          <ul className="max-h-80 overflow-y-auto py-2">
            {results.map((article, i) => (
              <li key={article.slug}>
                <button
                  className={`w-full px-4 py-2.5 text-left transition-colors ${
                    i === activeIdx ? 'bg-accent' : 'hover:bg-accent/50'
                  }`}
                  onClick={() => navigate(article.slug)}
                  onMouseEnter={() => setActiveIdx(i)}
                >
                  <p className="text-sm leading-snug font-medium">{article.title}</p>
                  <p className="text-muted-foreground mt-0.5 line-clamp-1 text-xs">
                    {article.summary}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        ) : query ? (
          <p className="text-muted-foreground px-4 py-6 text-center text-sm">
            No results for &ldquo;{query}&rdquo;
          </p>
        ) : (
          <p className="text-muted-foreground px-4 py-6 text-center text-sm">
            Start typing to search…
          </p>
        )}
      </div>
    </div>
  )
}
